/**
 * Brand colour safety — Step 135 (the plan's white-label rule).
 *
 * *"Refuse to save a brand colour that can't reach a readable contrast
 * against both light and dark surfaces. Otherwise a tenant picks #FFD400,
 * their portal becomes unreadable, and it is your support ticket."*
 *
 * The brand colour paints buttons and links, so the bar is WCAG's 3:1 for
 * UI components, checked against BOTH surfaces the product ships. 4.5:1
 * (body text) against both at once is a mid-tone band almost no real brand
 * lands in; 3:1 is the honest requirement for what the colour actually does.
 */

export const LIGHT_SURFACE = '#ffffff';
export const DARK_SURFACE = '#0b1220';

export function parseHex(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function luminance([r, g, b]: [number, number, number]): number {
  const c = [r, g, b].map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}

export function contrastRatio(a: string, b: string): number {
  const pa = parseHex(a),
    pb = parseHex(b);
  if (!pa || !pb) return 0;
  const la = luminance(pa),
    lb = luminance(pb);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** Why this colour cannot be the brand, or null if it can. */
export function brandColourError(hex: string): string | null {
  if (!parseHex(hex)) return 'Use a six-digit hex colour, like #2563eb.';
  const light = contrastRatio(hex, LIGHT_SURFACE);
  const dark = contrastRatio(hex, DARK_SURFACE);
  if (light < 3 && dark < 3) return 'That colour is too faint against both light and dark backgrounds.';
  if (light < 3) return `That colour is too light to read against white (${light.toFixed(1)}:1; needs 3:1).`;
  if (dark < 3)
    return `That colour is too dark to read against the dark theme (${dark.toFixed(1)}:1; needs 3:1).`;
  return null;
}
